import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useWheelScrollPassthrough } from '../hooks/useWheelScrollPassthrough';
import './CategoryIconCards.css';

/**
 * CategoryIconCards - Visual category filter with icon cards
 *
 * @see GitHub Issue #290 - Scenario organization
 */

// Map categories to icons - add new categories here as needed
const CATEGORY_ICONS = {
  'all': '🎯',
  // Production categories
  'Healthcare (Professional)': '🏥',
  'Sales & Clients': '🤝',
  'Personal & Family': '👨‍👩‍👧',
  'Ethics & Integrity': '⚖️',
  'Career Progression': '📈',
  'Workplace': '💼',
  'Management & Leadership': '👑',
  'Financial Services': '💰',
  'Customer Service': '💬',
  'Education & Students': '🎓',
  'Tech & Startups': '🚀',
  // Umuzi categories
  'Web Dev': '💻',
  'Project Management': '📋',
  // UNICEF categories
  'Community Health': '👩‍⚕️',
  'Child Protection': '🛡️',
  'Teachers': '👩‍🏫',
  'SBC': '📢',
  'Country Office': '🏛️',
  'Entrepreneurship': '💡',
  'Digital Competence': '🖥️',
  // Life Choices categories
  'Professional Identity': '🪪',
  'Technical Communication': '💻',
  'Client & Requirements': '🤝',
  'Code Review & Feedback': '🔍',
  'Ethics & Courage': '⚖️',
  'Business Communication': '💼',
  'Data & Analytics': '📊',
  'Salesforce (YouthForce)': '☁️',
  'Workplace Skills': '🏢',
  // Legacy categories (for backwards compat)
  'Workplace Scenarios': '💼',
  'Managing Difficult Conversations': '🗣️',
  'Sales & Negotiation': '🤝',
  'Leadership': '👑',
  'Collections with Dignity': '🤲',
  'VC Pitch Q&A': '🚀',
  'Interview Practice': '🎤',
  'Feedback & Reviews': '📋',
  'Conflict Resolution': '🕊️',
  'Coaching': '🏆',
  // Yenza categories
  'Networking': '🤝',
  'Career Conversations': '💬',
  'Career Mentor': '🎓',
  'Interview Prep': '🎯',
  // Fallback for unknown categories
  'default': '📁',
};

// Shorten long category names for display
const SHORT_NAMES = {
  'Healthcare (Professional)': 'Healthcare',
  'Sales & Clients': 'Sales',
  'Personal & Family': 'Personal',
  'Ethics & Integrity': 'Ethics',
  'Career Progression': 'Career',
  'Management & Leadership': 'Leadership',
  'Financial Services': 'Finance',
  'Customer Service': 'Customer',
  'Education & Students': 'Education',
  'Tech & Startups': 'Tech',
  // UNICEF
  'Community Health': 'Community Health',
  'Child Protection': 'Protection',
  'Country Office': 'Workplace',
  'Digital Competence': 'Digital',
  'Web Dev': 'Coding',
  // Life Choices
  'Client & Requirements': 'Client',
  'Code Review & Feedback': 'Code Review',
  'Ethics & Courage': 'Ethics',
  'Business Communication': 'Business',
  'Data & Analytics': 'Data',
  'Salesforce (YouthForce)': 'Salesforce',
  // Legacy
  'Managing Difficult Conversations': 'Difficult Talks',
  'Collections with Dignity': 'Collections',
  'Sales & Negotiation': 'Sales',
  'Workplace Scenarios': 'Workplace',
  'Interview Practice': 'Interviews',
  'Feedback & Reviews': 'Feedback',
  'Conflict Resolution': 'Conflict',
};

function CategoryIconCards({
  categories,
  selectedCategory,
  onSelect,
  scenarioCounts,
  totalCount,
  className = '',
}) {
  const getIcon = (category) => CATEGORY_ICONS[category] || CATEGORY_ICONS.default;
  const getShortName = (category) => SHORT_NAMES[category] || category;

  // #1842: shared across many pages (dashboard, community, /for/* pages) --
  // fix the wheel scroll trap once here rather than at every call site.
  const stripRef = useRef(null);
  useWheelScrollPassthrough([stripRef], [categories]);

  // #2547: the right-edge fade (CategoryIconCards.css) is the only cue that
  // more pills exist off-screen. It must hide whenever there is nothing left
  // to scroll TO -- either the strip never overflows at all (wide viewport:
  // scrollWidth <= clientWidth), or the user has already scrolled to its
  // end. The CSS class this drives (`scrolled-end`) predates this effect and
  // was previously applied by nothing (`git grep scrolled-end` had exactly
  // one hit, the CSS rule itself) -- the fade rendered unconditionally, which
  // is the reported defect: a wide-screen user sees the fade, tries to
  // scroll, finds nothing to scroll to, and concludes scrolling is broken.
  const [hideFade, setHideFade] = useState(true);

  useEffect(() => {
    const strip = stripRef.current;
    if (!strip) return undefined;

    // Subpixel rounding at fractional browser zoom can put scrollWidth a
    // fraction of a px above clientWidth even with nothing to scroll to.
    const FADE_EPSILON_PX = 1;

    const updateFadeState = () => {
      const noOverflow = strip.scrollWidth <= strip.clientWidth + FADE_EPSILON_PX;
      const atEnd = strip.scrollLeft + strip.clientWidth >= strip.scrollWidth - FADE_EPSILON_PX;
      setHideFade(noOverflow || atEnd);
    };

    updateFadeState();

    strip.addEventListener('scroll', updateFadeState, { passive: true });
    window.addEventListener('resize', updateFadeState);

    // #2547 review-fix: scroll + window-resize + `categories` are not enough,
    // and the gap is worse than the bug this effect fixes.
    //
    // `hideFade` is a CACHED measurement. Nothing above observes the strip's
    // OWN box, so any width change that fires neither a scroll nor a window
    // resize leaves the cache stale. The reachable case is a cold load:
    // index.html loads Inter with `display=swap`, so the first paint uses the
    // system fallback, the mount measurement runs against fallback metrics,
    // and the swap to Inter then widens the pills silently. At exactly the
    // boundary #2547 is about (staging measured scrollWidth === clientWidth
    // === 1152), a strip that measures no-overflow under the fallback and
    // overflows under Inter keeps `scrolled-end` forever — the fade never
    // appears and the user gets NO cue that pills exist off-screen. That is
    // the inverse of the reported defect and strictly worse than the
    // always-on fade this effect replaced.
    //
    // Same staleness for any container-width change without a window resize:
    // a page-level vertical scrollbar appearing as cards load below on
    // non-overlay-scrollbar platforms, or the `overflowY:'auto'` modal in
    // MyChatSessionsPage.jsx whose scrollbar appears as its content loads.
    //
    // Note the pre-existing sibling check in hooks/useWheelScrollPassthrough.js
    // is immune because it re-reads scrollWidth/clientWidth live per event.
    // Only a cached measurement can go stale, so only this one needs observing.
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      // Same instrument the repo already uses in lib/composerFabClearance.js.
      ro = new ResizeObserver(updateFadeState);
      ro.observe(strip);
    }

    // Fonts land after first paint even when the box does not change size
    // (e.g. the strip is width-constrained but its CONTENT reflows). Guarded:
    // `document.fonts` is absent in the jsdom test lane and in older browsers.
    let cancelled = false;
    if (typeof document !== 'undefined' && document.fonts?.ready?.then) {
      document.fonts.ready.then(() => {
        if (!cancelled) updateFadeState();
      }).catch(() => {});
    }

    return () => {
      cancelled = true;
      if (ro) ro.disconnect();
      strip.removeEventListener('scroll', updateFadeState);
      window.removeEventListener('resize', updateFadeState);
    };
    // categories: strip content (and therefore scrollWidth) changes with it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories]);

  return (
    <div className={`category-scroll-strip-container ${hideFade ? 'scrolled-end' : ''} ${className}`}>
      <div className="category-scroll-strip" ref={stripRef}>
        {/* All categories pill */}
        <button
          className={`category-pill ${selectedCategory === 'all' ? 'selected' : ''}`}
          onClick={() => onSelect('all')}
          type="button"
        >
          <span className="category-pill-icon">{getIcon('all')}</span>
          <span className="category-pill-label">All</span>
          <span className="category-pill-count">{totalCount}</span>
        </button>

        {/* Individual category pills */}
        {categories.map((category) => (
          <button
            key={category}
            className={`category-pill ${selectedCategory === category ? 'selected' : ''}`}
            onClick={() => onSelect(category)}
            type="button"
          >
            <span className="category-pill-icon">{getIcon(category)}</span>
            <span className="category-pill-label">{getShortName(category)}</span>
            <span className="category-pill-count">{scenarioCounts[category] || 0}</span>
          </button>
        ))}
      </div>
      <div className="category-scroll-fade" aria-hidden="true" />
    </div>
  );
}

CategoryIconCards.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedCategory: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
  scenarioCounts: PropTypes.object.isRequired,
  totalCount: PropTypes.number.isRequired,
  className: PropTypes.string,
};

export default CategoryIconCards;
